import { Eye, EyeOff, LogIn } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ErroApi } from '../../lib/api';
import { useSessao } from '../../lib/sessao';
import { PrimaryButton } from './primary-button';
import { TextField } from './text-field';

/**
 * Lado do formulário do login. Toda a lógica de autenticação que já existia
 * em `paginas/entrar.tsx` (chamada a `entrar`, tratamento de `ErroApi`,
 * estado de carregando) — só a camada visual mudou. `lib/sessao.ts` e
 * `lib/api.ts` não foram tocados.
 *
 * Fundo levemente mais claro que o `BrandPanel` ao lado: a separação entre
 * os dois é por ELEVAÇÃO (`bg-auth-superficie/[0.02]` a mais aqui em cima da
 * superfície do cartão), não por borda dura entre as colunas.
 */
export function LoginForm(): JSX.Element {
  const entrar = useSessao((s) => s.entrar);
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [senhaVisivel, setSenhaVisivel] = useState(false);

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErro(null);
    setEnviando(true);
    try {
      await entrar(email, senha);
    } catch (falha) {
      setErro(falha instanceof ErroApi ? falha.message : 'Não foi possível entrar.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <section className="bg-auth-superficie/2 relative flex flex-col justify-center p-8 sm:p-10 md:p-12">
      <form onSubmit={(e) => void enviar(e)} className="w-full">
        <h2 className="text-auth-texto text-2xl font-light tracking-tight">
          Acessar o painel
        </h2>
        <p className="text-auth-texto-secundario mt-1.5 mb-7 text-sm">
          Use o e-mail do seu escritório.
        </p>

        {erro && (
          <div
            role="alert"
            aria-live="polite"
            className="bg-auth-erro/10 border-auth-erro/25 text-auth-erro mb-5 rounded-auth-controle border px-4 py-3 text-sm"
          >
            {erro}
          </div>
        )}

        <div className="mb-4">
          <TextField
            id="email"
            rotulo="E-mail"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </div>

        <div className="mb-6">
          {/*
            O olho fica DENTRO do campo, com respiro à direita (`pr-11`) do
            tamanho do botão — senão a senha longa passa por baixo do ícone.

            O nome do botão NÃO contém "senha" nem "e-mail": `getByLabel`
            casa por SUBSTRING, e um segundo controle com essas palavras no
            nome faz `getByLabel('Senha')` casar com os dois. `aria-pressed`
            anuncia pra leitor de tela que a senha está exposta antes de
            digitar num lugar público.
          */}
          <TextField
            id="senha"
            rotulo="Senha"
            type={senhaVisivel ? 'text' : 'password'}
            autoComplete="current-password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            className="pr-11"
            trailing={
              <button
                type="button"
                onClick={() => setSenhaVisivel((v) => !v)}
                aria-pressed={senhaVisivel}
                aria-label={
                  senhaVisivel
                    ? 'Ocultar o que foi digitado'
                    : 'Exibir o que foi digitado'
                }
                title={senhaVisivel ? 'Ocultar' : 'Exibir'}
                className="text-auth-texto-secundario hover:text-auth-texto focus-visible:ring-auth-marca-hover/35 absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-auth-controle transition-colors focus-visible:ring-2 focus-visible:outline-none"
              >
                {senhaVisivel ? (
                  <EyeOff className="size-[18px]" aria-hidden />
                ) : (
                  <Eye className="size-[18px]" aria-hidden />
                )}
              </button>
            }
          />
        </div>

        <PrimaryButton type="submit" carregando={enviando}>
          {!enviando && <LogIn className="size-4" aria-hidden />}
          {enviando ? 'Entrando…' : 'Entrar'}
        </PrimaryButton>

        {/*
          A recuperação ainda não existe no servidor, de propósito: acesso
          liberado POR ESCRITÓRIO, quem devolve a senha é o responsável pela
          conta, não um e-mail automático.
        */}
        <p className="mt-5 text-center text-sm">
          <a
            href="mailto:suporte@specializer.com.br?subject=Recupera%C3%A7%C3%A3o%20de%20acesso%20%C2%B7%20Specializer%20Social"
            className="text-auth-marca-hover hover:text-auth-acento focus-visible:ring-auth-marca-hover/35 rounded font-medium underline-offset-4 transition-colors hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            Esqueceu sua senha? Clique aqui
          </a>
        </p>

        {/*
          O caminho de quem AINDA não tem escritório. Antes do autocadastro
          esta tela era um beco para quem chegava sem conta: a única saída era
          escrever para o suporte.
        */}
        <p className="text-auth-texto-secundario mt-2 text-center text-sm">
          Ainda não tem escritório?{' '}
          <Link
            to="/criar-conta"
            className="text-auth-marca-hover hover:text-auth-acento focus-visible:ring-auth-marca-hover/35 rounded font-medium underline-offset-4 transition-colors hover:underline focus-visible:ring-2 focus-visible:outline-none"
          >
            Criar agora, grátis por 7 dias
          </Link>
        </p>

        <div className="border-auth-borda/10 text-auth-texto-secundario mt-6 border-t pt-4 text-xs leading-relaxed">
          <strong className="text-auth-texto font-medium">Acesso por escritório.</strong>{' '}
          Os contratos e as alterações ficam dentro do escritório que os criou. A senha é
          devolvida pelo responsável pela conta.
        </div>
      </form>
    </section>
  );
}
